// GPP Scoreboard — the internal "how is the program doing" view Chris
// Karabinos asked for (2026-10): how many Growth Partners sit in each program
// stage (Monthly Check-ins split Month 1…12), how seasoned the network is,
// and per partner what it produced — referrals, deals, closed business,
// recorded fees.
//
// Data: GET /api/gpp-partners/scoreboard (app/gpp_scoreboard.py), computed
// live — the cached referral counters on gpp_partners are stale and unused.
// Revenue is recorded closing fees (deal_closings fee + follow-on) on Won
// deals only; a Won deal with no closing recorded adds $0.
//
// Rendered as the "Scoreboard" tab of the GPP Partners page (GppView in
// buyers_gpp.jsx). Attached to window: GppScoreboard.

// Strip groups, in program order. Keys are app/gpp_scoreboard.BUCKETS.
const SB_GROUPS = [
  { label: 'Lead funnel',       hue: 'slate', test: (k) => k === 'pre_program' },
  { label: 'Onboarding calls',  hue: 'amber', test: (k) => k === 'exploratory' || k === 'insights' || k === 'matrix' },
  { label: 'Monthly check-ins', hue: 'green', test: (k) => k.startsWith('month_') },
  { label: 'Graduated',         hue: 'teal',  test: (k) => k === 'onboarded' },
];
const sbHue = (key) => (SB_GROUPS.find((g) => g.test(key || '')) || SB_GROUPS[0]).hue;

// Fees read like the rest of the deal money in Atlas, lower-case k: $320k, $1.2M.
const fmtFee = (n) => {
  if (!n) return '—';
  const k = Math.round(n / 1e3);
  if (k >= 1000) return `$${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (k >= 1) return `$${k}k`;
  return `$${Math.round(n)}`;
};

// One catalog for header + body. `num` columns open descending on first
// click (most first); text columns open A→Z.
const SB_COLUMNS = [
  { key: 'name',         label: 'Partner',       w: 200 },
  { key: 'firm',         label: 'Firm',          w: 200 },
  { key: 'stage',        label: 'Stage',         w: 160, num: true },
  { key: 'referrals',    label: 'Referrals',     w: 90,  num: true, align: 'right' },
  { key: 'deals',        label: 'Deals',         w: 110, num: true, align: 'right' },
  { key: 'won',          label: 'Won',           w: 70,  num: true, align: 'right' },
  { key: 'revenue',      label: 'Revenue',       w: 100, num: true, align: 'right' },
  { key: 'lastReferral', label: 'Last referral', w: 120, num: true },
  { key: 'matrix',       label: 'Matrix rows',   w: 116, num: true, align: 'right', last: true },
];

const SB_SORT_EXTRACT = {
  name:         (r) => (r.name || '').toLowerCase() || null,
  firm:         (r) => (r.firm || '').toLowerCase() || null,
  stage:        (r) => r.stage_order,
  referrals:    (r) => r.referrals || 0,
  deals:        (r) => r.deals || 0,
  won:          (r) => r.won_deals || 0,
  revenue:      (r) => r.revenue_usd || 0,
  lastReferral: (r) => r.last_referral_date || null,
  matrix:       (r) => (r.portal_owner_count == null ? null : r.portal_owner_count),
};

const sbCell = (col, content, opts = {}) => (
  <div key={col.key} title={opts.title}
    style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center',
             justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start',
             minWidth: 0, fontSize: 12, color: opts.color || 'var(--text-2)',
             borderRight: col.last ? 'none' : '1px solid var(--border)',
             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    className={col.align === 'right' ? 'num' : ''}>
    {content}
  </div>
);

const sbRenderCell = (r, col) => {
  const zero = 'var(--text-4)';
  switch (col.key) {
    case 'name':
      return (
        <div key={col.key} style={{ width: col.w, padding: '6px 10px', minWidth: 0,
                                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                    borderRight: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.name}
          </div>
          {r.owner_name && (
            <div style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.owner_name}
            </div>
          )}
        </div>
      );
    case 'firm':
      return sbCell(col, r.firm || '—', { color: r.firm ? 'var(--text-2)' : zero, title: r.firm || undefined });
    case 'stage':
      return sbCell(col, (
        <span title={r.gpp_stage || 'No stage set'}>
          <Pill hue={r.in_program ? sbHue(r.stage_key) : (r.stage_key === 'pre_program' ? 'slate' : 'rose')} sz="sm">
            {r.stage_label}
          </Pill>
        </span>
      ));
    case 'referrals':
      return sbCell(col, r.referrals || '—', { color: r.referrals ? 'var(--text)' : zero });
    case 'deals':
      return sbCell(col, r.deals ? (
        <span>
          {r.deals}
          {r.live_deals > 0 && <span style={{ color: 'var(--text-3)', fontSize: 11 }}> · {r.live_deals} live</span>}
        </span>
      ) : '—', { color: r.deals ? 'var(--text)' : zero });
    case 'won':
      return sbCell(col, r.won_deals || '—', { color: r.won_deals ? 'var(--green-fg)' : zero });
    case 'revenue':
      return sbCell(col, fmtFee(r.revenue_usd), {
        color: r.revenue_usd ? 'var(--text)' : zero,
        title: r.won_deals && !r.revenue_usd ? 'Won, but no closing fee recorded yet' : undefined,
      });
    case 'lastReferral':
      return sbCell(col, r.last_referral_date ? window.fmtDate(r.last_referral_date) : '—',
        { color: r.last_referral_date ? 'var(--text-2)' : zero });
    case 'matrix':
      return sbCell(col, r.portal_owner_count == null ? '—' : r.portal_owner_count,
        { color: r.portal_owner_count ? 'var(--text-2)' : zero, title: 'Business owners in their Network Matrix (partner portal)' });
    default:
      return sbCell(col, null);
  }
};

// Same chrome as GppView's KPI strip, plus an optional subline.
const SbKpi = ({ label, value, sub, title }) => (
  <div title={title} style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                              borderRadius: 6, padding: '8px 12px', minWidth: 0 }}>
    <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                   letterSpacing: '0.05em', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{sub}</div>}
  </div>
);

const SbCaption = ({ children, title }) => {
  const caption = (
    <div title={title} style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase',
                                letterSpacing: '0.05em', margin: '0 0 6px 2px' }}>
      {children}
    </div>
  );
  return caption;
};

const GppScoreboard = ({ setContactId }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  // Default: revenue desc — the server's own order (revenue → won →
  // referrals), so ties under any other sort keep that order too.
  const [sortKey, setSortKey] = React.useState('revenue');
  const [sortDir, setSortDir] = React.useState('desc');

  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.gppScoreboard()
      .then((r) => { if (alive) setData(r || null); })
      .catch((e) => { if (alive) setError(e && e.message ? e.message : 'Could not load the scoreboard'); });
    return () => { alive = false; };
  }, []);

  const toggleSort = (key) => {
    if (key === sortKey) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return; }
    const col = SB_COLUMNS.find((c) => c.key === key);
    setSortKey(key);
    setSortDir(col && col.num ? 'desc' : 'asc');
  };

  const rows = (data && data.partners) || [];
  const sorted = React.useMemo(() => (
    [...rows].sort(window.makeComparator(sortKey, sortDir, SB_SORT_EXTRACT))
  ), [rows, sortKey, sortDir]);

  if (error) {
    return (
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                       padding: 24, fontSize: 12.5, color: 'var(--rose-fg)' }}>
          Couldn't load the scoreboard — {error}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                       padding: 24, fontSize: 12, color: 'var(--text-3)' }}>
          Loading…
        </div>
      </div>
    );
  }

  const s = data.seasoning || {};
  const t = data.totals || {};
  const stages = data.stages || [];
  const off = (data.off_program || []).filter((o) => o.count > 0);
  const n = (v) => (v == null ? '—' : Number(v).toLocaleString());

  return (
    <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)', padding: 16 }}>
      {/* Stage strip — every bucket in program order, zeros included, so the
          shape of the funnel reads at a glance. */}
      <SbCaption title="Partners per program stage, from the GPP stage on each partner (any spelling DealCloud or the portal has used).">
        Where partners are
      </SbCaption>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                     padding: 10, marginBottom: 6, overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 14, minWidth: 'max-content' }}>
          {SB_GROUPS.map((g) => {
            const tiles = stages.filter((st) => g.test(st.key));
            if (!tiles.length) return null;
            return (
              <div key={g.label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase',
                               letterSpacing: '0.05em', fontWeight: 500 }}>{g.label}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {tiles.map((st) => (
                    <div key={st.key} title={`${st.label}: ${st.count}`}
                      style={{ minWidth: st.key === 'pre_program' ? 112 : (st.key.startsWith('month_') ? 62 : 92),
                               padding: '6px 8px', borderRadius: 6,
                               background: st.count ? `var(--${g.hue}-bg)` : 'var(--surface-2)',
                               border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 11, color: st.count ? `var(--${g.hue}-fg)` : 'var(--text-4)',
                                     whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {st.label}
                      </div>
                      <div className="num" style={{ fontSize: 18, fontWeight: 600,
                                                     color: st.count ? 'var(--text)' : 'var(--text-4)' }}>
                        {st.count.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', margin: '0 0 16px 2px' }}>
        {off.length
          ? <>Off program: {off.map((o) => `${o.count.toLocaleString()} ${o.label.toLowerCase()}`).join(' · ')}</>
          : 'No disqualified or inactive partners.'}
        {' · '}{n(t.partners)} partners in all
      </div>

      {/* Seasoning — how far into the program the network is. */}
      <SbCaption>How seasoned the network is</SbCaption>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 16 }}>
        <SbKpi label="In program" value={n(s.in_program)} sub="Exploratory → Fully Onboarded" />
        <SbKpi label="Onboarding calls" value={n(s.onboarding)} sub="Exploratory · Insights · Matrix" />
        <SbKpi label="Months 1–6" value={n((s.months_1_3 || 0) + (s.months_4_6 || 0))}
          sub={`1–3: ${n(s.months_1_3)} · 4–6: ${n(s.months_4_6)}`} />
        <SbKpi label="Months 7–12" value={n((s.months_7_9 || 0) + (s.months_10_12 || 0))}
          sub={`7–9: ${n(s.months_7_9)} · 10–12: ${n(s.months_10_12)}`} />
        <SbKpi label="Fully onboarded" value={n(s.onboarded)} sub="Past Month 12" />
        <SbKpi label="Median check-in" value={s.median_checkin_month == null ? '—' : `Month ${s.median_checkin_month}`}
          sub={`across ${n(s.in_checkins)} in monthly check-ins`} />
      </div>

      {/* Outcomes — what the program is evaluated on. */}
      <SbCaption title="Referrals = owners a partner referred (contacts whose referral source is the partner). Deals = engagements on those owners' companies (buyer shells excluded). Revenue = recorded closing fees on Won deals.">
        What it produced
      </SbCaption>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 16 }}>
        <SbKpi label="Referrals" value={n(t.referrals)} sub={`from ${n(t.referring_partners)} partners`} />
        <SbKpi label="Deals" value={n(t.deals)} sub={`${n(t.live_deals)} live`} />
        <SbKpi label="Won" value={n(t.won_deals)} sub="closed business" />
        <SbKpi label="Revenue" value={fmtFee(t.revenue_usd)} sub="recorded closing fees" />
      </div>

      {/* Per-partner table. */}
      <SbCaption title="Everyone in the program, plus anyone outside it (a lead, a disqualified or inactive partner) who has referred an owner.">
        Partners · {n(rows.length)}
      </SbCaption>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                     borderRadius: 8, overflowX: 'auto' }}>
        {rows.length === 0 ? (
          <div style={{ padding: 32, fontSize: 12.5, color: 'var(--text-3)' }}>
            No partners in the program yet, and none outside it have referred an owner.
          </div>
        ) : (
          <div style={{ minWidth: 'max-content' }}>
            <div style={{ display: 'flex', height: 30, background: 'var(--surface-2)',
                          fontSize: 11, color: 'var(--text-3)', fontWeight: 500,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          borderBottom: '1px solid var(--border)' }}>
              {SB_COLUMNS.map((c) => (
                <window.SortableHeader key={c.key} col={c}
                  sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              ))}
            </div>
            {sorted.map((r) => (
              <div key={r.id}
                onClick={() => r.contact_id && setContactId && setContactId(r.contact_id)}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface)'}
                style={{ display: 'flex', minHeight: 44, alignItems: 'stretch',
                         borderBottom: '1px solid var(--border)', background: 'var(--surface)',
                         cursor: r.contact_id ? 'pointer' : 'default' }}>
                {SB_COLUMNS.map((col) => sbRenderCell(r, col))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

window.GppScoreboard = GppScoreboard;
